import AddToPhotosIcon from '@mui/icons-material/AddToPhotos';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import DeleteSweepTwoToneIcon from '@mui/icons-material/DeleteSweepTwoTone';
import {
  Avatar,
  Box,
  Grid,
  Stack,
  SwipeableDrawer,
  Typography,
  useTheme,
} from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';
import { CommonBtn } from '../../components';
import SearchInput from '../../components/Input/SearchInput/SearchInput';
import { useAppDispatch, useAppSelector } from '../../redux-store/hooks';
import { deleteCategory, getCategoryList } from '../../services';
import { AddCategory } from './modules';

const Category: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  
  const categories = useAppSelector((state) => state.categories.data || []);
  const STANDARD_HEADER_HEIGHT = useAppSelector((state) => state.ui.STANDARD_HEADER_HEIGHT);

  const [searchTxt, setSearchTxt] = useState('');
  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);

  useEffect(() => {
    dispatch(getCategoryList());
  }, [dispatch]);

  const handleOpenDrawer = (category?: any) => {
    setSelectedCategory(category || null);
    setOpenDrawer(true);
  };

  const filteredCategories = categories.filter((cat: any) =>
    cat.categoryName.toLowerCase().includes(searchTxt.toLowerCase())
  );

  const columns: GridColDef[] = [
    {
      field: 'categoryName',
      headerName: 'Category Details',
      flex: 1.5,
      renderCell: (params) => (
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            src={params.row.categoryIcon}
            variant="rounded"
            sx={{
              width: 44,
              height: 44,
              bgcolor: theme.palette.primary.main + '10',
              borderRadius: '12px',
              border: `1px solid ${theme.palette.divider}`,
              p: 0.5
            }}
          >
            {params.value?.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={700}>
              {params.value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {params.row.categoryId?.slice(-6).toUpperCase()}
            </Typography>
          </Box>
        </Stack>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      flex: 0.5,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditTwoToneIcon sx={{ color: theme.palette.primary.main }} />}
          label="Edit"
          onClick={() => handleOpenDrawer(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteSweepTwoToneIcon sx={{ color: theme.palette.error.main }} />}
          label="Delete"
          onClick={() => dispatch(deleteCategory(params.row.categoryId))}
        />,
      ],
    },
  ];

  const PANEL_HEIGHT = `calc(100vh - ${STANDARD_HEADER_HEIGHT}px)`;

  return (
    <Box sx={{ height: PANEL_HEIGHT, display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {/* Header Section */}
      <Grid container sx={{ p: 3, alignItems: 'center', justifyContent: 'space-between' }}>
        <Grid item xs={12} md={4}>
          <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
            Product Categories
          </Typography>
          <SearchInput
            value={searchTxt}
            onChange={(txt) => setSearchTxt(txt)}
            placeholder="Search categories..."
          />
        </Grid>
        
        <Grid item display="flex" alignItems="center" gap={3}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h6" fontWeight={800} lineHeight={1}>
              {categories.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
              Total Groups
            </Typography>
          </Box>
          <CommonBtn
            variant="contained"
            startIcon={<AddToPhotosIcon />}
            onClick={() => handleOpenDrawer()}
            sx={{ borderRadius: '12px', px: 3 }}
          >
            Add Category
          </CommonBtn>
        </Grid>
      </Grid>

      {/* Main Content Area */}
      <Box sx={{ flex: 1, px: 3, pb: 3 }}>
        <Box sx={{ 
          height: '100%', 
          bgcolor: 'background.paper', 
          //borderRadius: '20px', 
          overflow: 'hidden',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: '0 4px 20px rgba(0,0,0,0.03)'
        }}>
          <DataGrid
            rows={filteredCategories}
            columns={columns}
            getRowId={(row) => row.categoryId}
            disableRowSelectionOnClick
            rowHeight={70}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell:focus': { outline: 'none' },
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: theme.palette.grey[50],
                borderBottom: `1px solid ${theme.palette.divider}`,
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                letterSpacing: 1,
                fontWeight: 700
              },
              '& .MuiDataGrid-row:hover': {
                bgcolor: theme.palette.primary.main + '05',
              }
            }}
          />
        </Box>
      </Box>

      {/* Slide-out Management Drawer */}
      <SwipeableDrawer
        anchor="right"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        onOpen={() => setOpenDrawer(true)}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 480 },
            backgroundColor: theme.palette.background.default,
            top: STANDARD_HEADER_HEIGHT,
            height: `calc(100% - ${STANDARD_HEADER_HEIGHT}px)`,
            boxShadow: '-10px 0px 40px rgba(0,0,0,0.1)',
            backgroundImage: 'none',
          },
        }}
      >
        <Box sx={{ p: 4 }}>
          <Typography variant="h5" fontWeight={800} gutterBottom>
            {selectedCategory ? "Edit Category" : "New Category"}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={4}>
            Set up your category name and icon to organize your digital menu.
          </Typography>

          {/* Reusing your module with the shared drawer layout */}
          <AddCategory
            open={openDrawer}
            toggleDailog={setOpenDrawer}
            editData={selectedCategory}
          />
        </Box>
      </SwipeableDrawer>
    </Box>
  );
};

export { Category };